import { useCallback, useRef, useState } from "react"
import { Intercom, show, shutdown } from "@intercom/messenger-js-sdk"
import { PSApiOperations } from "@/api/PSApiOperations"
import { isPixiesetAuthEnabled } from "@/utils/measurement/feature-flag"

const log = (...args: any[]) => console.log("[Intercom]", ...args)
const error = (...args: any[]) => console.error("[Intercom]", ...args)

export const useIntercom = () => {
	const [isInitialized, setIsInitialized] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [errorState, setErrorState] = useState<string | null>(null)
	const initializationAttempted = useRef(false)
	const initPromiseRef = useRef<Promise<boolean> | null>(null)

	const initialize = useCallback(async () => {
		if (!isPixiesetAuthEnabled()) {
			return
		}

		if (initializationAttempted.current) {
			return
		}

		initializationAttempted.current = true
		setIsLoading(true)

		initPromiseRef.current = (async () => {
			try {
				// Fetch Intercom data
				const data = await PSApiOperations.getIntercomData()

				if (!data.app_id || !data.user_id || !data.user_hash) {
					throw new Error("Missing required Intercom data")
				}

				// Initialize (auto boot)
				Intercom({
					api_base: "https://api-iam.intercom.io",
					app_id: data.app_id,
					user_id: data.user_id.toString(),
					name: data.username,
					email: data.email,
					created_at: Math.floor(new Date(data.created_at).getTime() / 1000),
					user_hash: data.user_hash,
					hide_default_launcher: data.hide_default_launcher,
					client_gallery_storage: data.client_gallery_storage,
					client_gallery_subscription: data.client_gallery_subscription,
					website_subscription: data.website_subscription,
					studio_manager_subscription: data.studio_manager_subscription,
				})

				setIsInitialized(true)
				setErrorState(null)
				log("Initialization complete")
				return true
			} catch (err) {
				const errorMsg = err instanceof Error ? err.message : String(err)
				error("Initialization failed:", errorMsg)
				setErrorState(errorMsg)
				setIsInitialized(false)
				initializationAttempted.current = false
				return false
			} finally {
				setIsLoading(false)
				// Clear the promise ref once initialization is settled (success or failure),
				initPromiseRef.current = null
			}
		})()

		await initPromiseRef.current
	}, [])

	// Open the Messenger
	const openIntercom = useCallback(async () => {
		if (!isPixiesetAuthEnabled()) {
			return
		}

		// If initialization is in progress, wait for it to complete
		if (!isInitialized && initPromiseRef.current) {
			const success = await initPromiseRef.current
			if (!success) {
				return
			}
		} else if (!isInitialized) {
			return
		}

		try {
			show()
			log("Messenger shown")
		} catch (e) {
			error("Error showing messenger:", e)
		}
	}, [isInitialized])

	// Shutdown Intercom (maybe call on logout)
	const shutdownIntercom = useCallback(() => {
		try {
			shutdown()
			setIsInitialized(false)
			initializationAttempted.current = false
		} catch (e) {
			error("Intercom shutdown error:", e)
		}
	}, [])

	return {
		isInitialized,
		isLoading,
		error: errorState,
		initialize,
		openIntercom,
		shutdown: shutdownIntercom,
	}
}
